import React, { useEffect, useState } from 'react';
import { userService } from '../../services/user';
import { LearningProgress } from '../../types';
import {
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  VideoCameraIcon,
  TrophyIcon,
  FireIcon,
  AcademicCapIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { videoService } from '../../services/videoService';
import { Video } from '../../types';
import { getImageUrl } from '../../services/api';

const Progress: React.FC = () => {
  const [progress, setProgress] = useState<LearningProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const data = await userService.getLearningProgress();
      setProgress(data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!progress) {
    return <div className="text-center py-12">No progress data available</div>;
  }

  const { overallProgress, progressByCategory, recentActivity } = progress;

  // Calculate streak (example: consecutive days of learning)
  const learningStreak = Math.floor(overallProgress.totalWatchTime / 86400); // Example calculation

  return (
    <div className="px-4 sm:px-0 max-w-7xl mx-auto">
      {/* Header with gradient background */}
      <div className="mb-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold">ການເດີນທາງການຮຽນຮູ້ຂອງຂ້ອຍ</h1>
            <p className="mt-2 text-indigo-100">ຕິດຕາມຄວາມຄືບຫນ້າຂອງທ່ານ ແລະ ສະເຫຼີມສະຫຼອງຜົນສໍາເລັດ</p>
          </div>
          <TrophyIcon className="h-16 w-16 text-yellow-300 opacity-50" />
        </div>
      </div>

      {/* Key Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Course Progress Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 transform hover:scale-105 transition-transform">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-indigo-100 rounded-full p-3">
              <ChartBarIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <span className="text-3xl font-bold text-gray-900">{overallProgress.progressPercentage}%</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">ຄວາມຄືບຫນ້າທັງໝົດ</h3>
          <p className="text-sm text-gray-500">ສືບຕໍ່ໄປ!</p>
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${overallProgress.progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Videos Watched Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 transform hover:scale-105 transition-transform">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-blue-100 rounded-full p-3">
              <VideoCameraIcon className="h-8 w-8 text-blue-600" />
            </div>
            <span className="text-3xl font-bold text-gray-900">{overallProgress.watchedVideos}</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">ວິດີໂອທີ່ເບິ່ງແລ້ວ</h3>
          <p className="text-sm text-gray-500">ຈາກທັງໝົດ {overallProgress.totalVideos} ວິດີໂອ</p>
          <div className="mt-3">
            <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
              {Math.round((overallProgress.watchedVideos / overallProgress.totalVideos) * 100)}% watched
            </span>
          </div>
        </div>

        {/* Completed Videos Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 transform hover:scale-105 transition-transform">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-green-100 rounded-full p-3">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <span className="text-3xl font-bold text-gray-900">{overallProgress.completedVideos}</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">ສຳເລັດແລ້ວ</h3>
          <p className="text-sm text-gray-500">ຄວາມຄືບຫນ້າທີ່ດີ!</p>
          <div className="mt-3">
            <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
              {Math.round((overallProgress.completedVideos / overallProgress.totalVideos) * 100)}% completion rate
            </span>
          </div>
        </div>

        {/* Learning Time Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 transform hover:scale-105 transition-transform">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-purple-100 rounded-full p-3">
              <ClockIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-gray-900">
                {Math.floor(overallProgress.totalWatchTime / 3600)}
              </span>
              <span className="text-lg font-semibold text-gray-600">h</span>
              <span className="text-xl font-bold text-gray-900 ml-1">
                {Math.floor((overallProgress.totalWatchTime % 3600) / 60)}
              </span>
              <span className="text-lg font-semibold text-gray-600">m</span>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">ເວລາຊົມທັງໝົດ</h3>
          <p className="text-sm text-gray-500">ຮຽນຮູ້ຕໍ່ໄປ!</p>
        </div>
      </div>

      {/* Achievement Badges */}
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 mb-8 border border-yellow-200">
        <div className="flex items-center mb-4">
          <TrophyIcon className="h-6 w-6 text-yellow-600 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">ຜົນສຳເລັດ</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {overallProgress.completedVideos >= 1 && (
            <div className="text-center">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <FireIcon className="h-10 w-10 text-orange-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">ກ້າວທຳອິດ</p>
                <p className="text-xs text-gray-500">ສຳເລັດ 1 ວິດີໂອ</p>
              </div>
            </div>
          )}
          {overallProgress.completedVideos >= 5 && (
            <div className="text-center">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <SparklesIcon className="h-10 w-10 text-purple-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">ດາວຂຶ້ນໃໝ່</p>
                <p className="text-xs text-gray-500">ສຳເລັດ 5 ວິດີໂອ</p>
              </div>
            </div>
          )}
          {overallProgress.completedVideos >= 10 && (
            <div className="text-center">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <AcademicCapIcon className="h-10 w-10 text-blue-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">ນັກຮຽນດີ</p>
                <p className="text-xs text-gray-500">ສຳເລັດ 10 ວິດີໂອ</p>
              </div>
            </div>
          )}
          {overallProgress.progressPercentage >= 100 && (
            <div className="text-center">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <TrophyIcon className="h-10 w-10 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">ຊະນະເລີດ</p>
                <p className="text-xs text-gray-500">ສຳເລັດ 100%</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Progress by Category with improved design */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <ChartBarIcon className="h-6 w-6 text-indigo-600 mr-2" />
            ຄວາມຄືບຫນ້າຕາມໝວດໝູ່
          </h2>
          <div className="space-y-4">
            {progressByCategory.map((category) => {
              const percentage = category.total_videos > 0
                ? Math.round((category.watched_videos / category.total_videos) * 100)
                : 0;

              return (
                <div key={category.category} className="relative">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium text-gray-900">{category.category}</h3>
                    <span className="text-sm text-gray-600">
                      {percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all duration-500 relative"
                      style={{ width: `${percentage}%` }}
                    >
                      <div className="absolute inset-0 bg-white opacity-20 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-gray-500">
                    <span>{category.watched_videos} / {category.total_videos} videos</span>
                    <span>{category.completed_videos} completed</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Activity with improved design */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <ClockIcon className="h-6 w-6 text-indigo-600 mr-2" />
            ການເຄື່ອນໄຫວຫຼ້າສຸດ
          </h2>
          {recentActivity.length === 0 ? (
            <div className="text-center py-8">
              <VideoCameraIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">ບໍ່ມີການເຄື່ອນໄຫວຫຼ້າສຸດ</p>
              <p className="text-sm text-gray-400 mt-1">ເລີ່ມຊົມວິດີໂອເພື່ອເບິ່ງຄວາມຄືບຫນ້າຂອງທ່ານທີ່ນີ້</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {recentActivity.map((video) => (
                <div key={video.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="relative flex-shrink-0">
                    {video.thumbnail_url ? (
                      <img
                        src={getImageUrl(video.thumbnail_url)}
                        alt={video.title}
                        className="w-16 h-10 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-10 bg-gray-300 rounded flex items-center justify-center">
                        <VideoCameraIcon className="h-5 w-5 text-gray-400" />
                      </div>
                    )}
                    {video.completed && (
                      <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5">
                        <CheckCircleIcon className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">{video.title}</h4>
                    <p className="text-xs text-gray-500">{video.category}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {video.completed ? (
                      <span className="inline-flex items-center text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        ສຳເລັດແລ້ວ
                      </span>
                    ) : (
                      <div>
                        <div className="text-xs font-medium text-gray-700">
                          {Math.round((video.watched_duration! / video.duration) * 100)}%
                        </div>
                        <div className="w-12 bg-gray-200 rounded-full h-1 mt-1">
                          <div
                            className="bg-indigo-600 h-1 rounded-full"
                            style={{ width: `${Math.round((video.watched_duration! / video.duration) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Motivational Message */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
        <div className="flex items-center">
          <SparklesIcon className="h-8 w-8 text-indigo-600 mr-4 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">ສືບຕໍ່ພັດທະນາຕົນເອງ!</h3>
            <p className="text-gray-600 mt-1">
              ທ່ານກຳລັງມີຄວາມຄືບຫນ້າທີ່ດີ. ຮັກສາຄວາມສອດຄ່ອງແລະທ່ານຈະບັນລຸເປົ້າໝາຍການຮຽນຮູ້ຂອງທ່ານ!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Progress; 